import React, { FC, useState } from 'react';
import {
  Box,
  Button,
  TextField,
  IconButton,
  makeStyles
} from '@material-ui/core';
import { ChromePicker } from 'react-color';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'src/store';
import {
  editText,
  editFontWeight,
  editColor,
  editFontSize,
  editFontFamily
} from 'src/store/modules/editText';
import useComponentVisible from 'src/hooks/useComponentVisible';
import colorRectangle from 'src/images/colorRectangle.png';
import AddIcon from '@material-ui/icons/Add';
import RemoveIcon from '@material-ui/icons/Remove';
import DeleteIcon from '@material-ui/icons/Delete';
import Plus from 'src/components/Icon/Plus';
import Minus from 'src/components/Icon/Minus';
import ChevronUp from 'src/components/Icon/ChevronUp';
import ChevronDown from 'src/components/Icon/ChevronDown';
import FontFamilyPicker from '../FontFamilyPicker';

const FONT_WEIGHT_ENUM: any = {
  300: 'Light',
  400: 'Regular',
  500: 'Medium',
  600: 'SemiBold',
  700: 'Bold',
  '': 'Custom'
};

const useStyles = makeStyles(() => ({
  root: {
    padding: '1.5rem 1rem',
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem'
  },
  header: {
    fontSize: '0.875rem',
    fontWeight: 600,
    fontFamily: 'Archivo',
    letterSpacing: '0.0175rem',
    marginBottom: '0.5rem'
  },
  itemsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem',
    maxHeight: '200px',
    overflowY: 'auto',
    padding: '0.5rem',
    border: '1px solid #D9D9D9',
    borderRadius: '4px'
  },
  itemRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem'
  },
  itemNumber: {
    minWidth: '24px',
    fontSize: '0.75rem',
    fontWeight: 500,
    color: '#666'
  },
  itemInput: {
    flex: 1,
    '& .MuiInputBase-input': {
      fontSize: '0.75rem',
      padding: '8px 12px'
    }
  },
  buttonContainer: {
    display: 'flex',
    gap: '0.5rem',
    marginTop: '0.5rem'
  },
  addButton: {
    fontSize: '0.75rem',
    textTransform: 'none',
    padding: '6px 16px'
  },
  info: {
    fontSize: '0.7rem',
    color: '#666',
    marginTop: '0.5rem',
    fontStyle: 'italic'
  },

  // Style editing styles
  bottomBtn: {
    padding: '0.5rem',
    display: 'flex',
    justifyContent: 'space-between',
    border: '1px solid #dddddd',
    backgroundColor: '#F2F2F2'
  },
  btnMinusPlus: {
    maxWidth: '1.5rem',
    minWidth: '1.5rem',
    maxHeight: '1.5rem',
    minHeight: '1.5rem',
    padding: 0,
    display: 'flex'
  },
  fontNumber: {
    width: '3rem',
    height: '1.5rem',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    fontFamily: 'Roboto',
    fontSize: '0.75rem',
    fontWeight: 400,
    borderRadius: '0.25rem',
    border: '1px solid #D9D9D9',
    backgroundColor: '#fff'
  },
  buttonWrapper: {
    maxWidth: '1.5rem',
    minWidth: '1.5rem',
    maxHeight: '1.5rem',
    minHeight: '1.5rem'
  },
  buttonImage: {
    maxWidth: '1.5rem',
    minWidth: '1.5rem',
    maxHeight: '1.5rem',
    minHeight: '1.5rem'
  },
  boxWrapper: {
    display: 'flex',
    justifyContent: 'space-between'
  },
  colorBox: {
    padding: '0.25rem 0.5rem',
    borderRadius: '0.25rem',
    border: '1px solid #D9D9D9',
    textAlign: 'center',
    color: '#000',
    fontFamily: 'Roboto',
    fontSize: '0.75rem',
    fontWeight: 400
  },
  fontBox: {
    width: '100%',
    height: '2.5rem',
    padding: '0.5rem',
    cursor: 'pointer',
    fontFamily: 'Roboto',
    fontSize: '0.75rem',
    fontWeight: 400,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    border: '1px solid #D9D9D9'
  }
}));

interface EditRouletteItemsProps {
  menuTitle?: string;
  type: string;
}

const EditRouletteItems: FC<EditRouletteItemsProps> = ({ menuTitle, type }) => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const text = useSelector((state: RootState) => state.editText.text);
  const fontSize = useSelector((state: RootState) => state.editText.fontSize);
  const fontWeight = useSelector(
    (state: RootState) => state.editText.fontWeight
  );
  const color = useSelector((state: RootState) => state.editText.color);
  const fontFamily = useSelector(
    (state: RootState) => state.editText.fontFamily
  );

  // Color picker state
  const {
    refPicker,
    refButton1,
    refButton2,
    isComponentVisible: colorShow,
    setIsComponentVisible: setColorShow
  } = useComponentVisible(false);
  const [isFontFamily, setIsFontFamily] = useState(false);

  // 기본값은 표시용으로만 사용, Redux 초기화는 BasicFullRoulette에서 처리
  const items = text?.[type] || [
    'coupon 10%',
    'coupon 20%',
    'coupon 30%',
    'coupon 40%',
    'coupon 50%',
    'coupon 60%'
  ];

  const handleItemChange = (index: number, value: string) => {
    const currentItems = text?.[type] || items;
    const newItems = [...currentItems];
    newItems[index] = value;
    const newText = { ...(text || {}), [type]: newItems };
    dispatch(editText(newText));
  };

  const handleAddItem = () => {
    const currentItems = text?.[type] || items;
    if (currentItems.length < 8) {
      const newItems = [...currentItems, `Item ${currentItems.length + 1}`];
      const newText = { ...(text || {}), [type]: newItems };
      dispatch(editText(newText));
    }
  };

  const handleRemoveItem = (index: number) => {
    const currentItems = text?.[type] || items;
    if (currentItems.length > 3) {
      const newItems = currentItems.filter((_: any, i: number) => i !== index);
      const newText = { ...(text || {}), [type]: newItems };
      dispatch(editText(newText));
    }
  };

  // Style handlers
  const handleFontSize = (f: number) => {
    const currentSize = fontSize?.[type] || 0.75;
    const newSize = currentSize + f;
    const newFontSize = { ...(fontSize || {}), [type]: newSize };
    dispatch(editFontSize(newFontSize));
  };

  const handleColor = (c: any) => {
    const newColor = { ...(color || {}), [type]: c.hex };
    dispatch(editColor(newColor));
  };

  const handleFontFamily = (family: string, weight: number | string) => {
    const newFontFamily = { ...(fontFamily || {}), [type]: family };
    const newFontWeight = { ...(fontWeight || {}), [type]: weight };
    dispatch(editFontFamily(newFontFamily));
    dispatch(editFontWeight(newFontWeight));
    setIsFontFamily(false);
  };

  return (
    <div className={classes.root}>
      <div
        style={{
          fontSize: '0.875rem',
          fontWeight: 600,
          fontFamily: 'Archivo',
          letterSpacing: '0.0175rem'
        }}
      >
        {menuTitle || 'ROULETTE ITEMS'}
      </div>

      {/* Font Family Selector */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '0.5rem'
        }}
      >
        <div
          className={classes.fontBox}
          onClick={() => setIsFontFamily(!isFontFamily)}
        >
          <span>{`${fontFamily?.[type] || 'Roboto'} [${FONT_WEIGHT_ENUM[
            fontWeight?.[type]
          ] || 'Regular'}]`}</span>
          {isFontFamily ? (
            <ChevronUp
              style={{
                width: '1.5rem',
                height: '1.5rem'
              }}
            />
          ) : (
            <ChevronDown
              style={{
                width: '1.5rem',
                height: '1.5rem'
              }}
            />
          )}
        </div>
        {isFontFamily && (
          <FontFamilyPicker
            oriFontFamily={fontFamily?.[type] || 'Roboto'}
            oriFontWeight={fontWeight?.[type] || 400}
            handleFontFamily={handleFontFamily}
          />
        )}
      </div>

      {/* Items List */}
      <div className={classes.itemsContainer}>
        {items.map((item: string, index: number) => (
          <div key={index} className={classes.itemRow}>
            <span className={classes.itemNumber}>{index + 1}.</span>
            <TextField
              className={classes.itemInput}
              value={item}
              onChange={e => handleItemChange(index, e.target.value)}
              variant="outlined"
              size="small"
              placeholder={`Item ${index + 1}`}
            />
            <IconButton
              size="small"
              onClick={() => handleRemoveItem(index)}
              disabled={items.length <= 3}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </div>
        ))}
      </div>

      {/* Add Item Button */}
      <div className={classes.buttonContainer}>
        <Button
          className={classes.addButton}
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={handleAddItem}
          disabled={items.length >= 8}
          size="small"
        >
          Add Item
        </Button>
        <Box flex={1} />
        <div className={classes.info}>
          {items.length}
          /8 items (min: 3, max: 8)
        </div>
      </div>

      {/* Style Controls - Font Size */}
      <div className={classes.bottomBtn}>
        <Box style={{ display: 'flex', gap: '1rem' }}>
          <Button
            className={classes.btnMinusPlus}
            disabled={fontSize?.[type] === 5 / 8}
            onClick={() => handleFontSize(-1 / 16)}
          >
            <Minus stroke={fontSize?.[type] === 5 / 8 ? '#BDBDBD' : '#000'} />
          </Button>
          <Button
            className={classes.btnMinusPlus}
            disabled={fontSize?.[type] === 4}
            onClick={() => handleFontSize(1 / 16)}
          >
            <Plus stroke={fontSize?.[type] === 4 ? '#BDBDBD' : '#000'} />
          </Button>
        </Box>
        <div className={classes.fontNumber}>
          {fontSize?.[type] ? fontSize[type] * 16 : 12}
        </div>
      </div>

      {/* Color Picker */}
      <Box className={classes.boxWrapper}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <Button
            ref={refButton1}
            onClick={() => setColorShow(!colorShow)}
            className={classes.buttonWrapper}
            style={{ padding: 0 }}
          >
            <img
              alt="color"
              src={colorRectangle}
              className={classes.buttonImage}
            />
          </Button>
          <Button
            ref={refButton2}
            style={{
              maxWidth: '1.25rem',
              minWidth: '1.25rem',
              maxHeight: '1.25rem',
              minHeight: '1.25rem',
              padding: 0
            }}
            onClick={() => setColorShow(!colorShow)}
          >
            <div
              style={{
                width: '1.25rem',
                height: '1.25rem',
                backgroundColor: color?.[type] || '#000000',
                border: '1px solid #D9D9D9',
                borderRadius: 999
              }}
            />
          </Button>
          <div className={classes.colorBox}>
            {(color?.[type] || '#000000').toUpperCase()}
          </div>
        </div>
      </Box>
      {colorShow && (
        <div ref={refPicker} style={{ width: '100%', marginTop: '1rem' }}>
          <ChromePicker
            color={color?.[type] || '#000000'}
            onChange={handleColor}
            disableAlpha
            width="100%"
          />
        </div>
      )}
    </div>
  );
};

export default EditRouletteItems;
